import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Badge, Collapse } from 'react-bootstrap';
import Button from "components/base/Button";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch,
  faUser,
  faTimes,
  faCheck,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { 
  UilEdit,
  UilTrash,
  UilClipboardNotes
} from '@iconscout/react-unicons';
import classNames from 'classnames';
import SearchBox from 'components/common/SearchBox';
import { dateFormatter } from 'helpers/utils';
import toast from 'react-hot-toast';
import NotesSkeleton from 'components/common/skeletons/NotesSkeleton';
import './InlineNotesList.css';

interface NoteItem {
  id: string;
  note: string;
  created_on: string;
  creator_type?: string;
  creator_id?: string;
}

interface InlineNotesListProps {
  notes: NoteItem[];
  isLoading: boolean;
  onCreateNote: (noteData: any) => Promise<void>;
  onUpdateNote?: (noteId: string, noteData: any) => Promise<void>;
  onDeleteNote?: (noteId: string) => Promise<void>;
}

const InlineNotesList: React.FC<InlineNotesListProps> = ({
  notes,
  isLoading,
  onCreateNote,
  onUpdateNote,
  onDeleteNote
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredNotes, setFilteredNotes] = useState<NoteItem[]>(notes);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editNoteContent, setEditNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const createFormRef = useRef<HTMLDivElement>(null);
  const newNoteTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredNotes(notes);
    } else {
      setFilteredNotes(
        notes.filter(note => 
          note.note.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
  }, [notes, searchQuery]);

  // Auto-focus when showing create form
  useEffect(() => {
    if (showCreateForm && newNoteTextareaRef.current) {
      newNoteTextareaRef.current.focus();
      // Scroll form into view
      createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showCreateForm]);

  const handleCreateNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Note content is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCreateNote({ note: newNoteContent.trim() });
      setNewNoteContent('');
      setShowCreateForm(false);
      toast.success('Note created successfully!');
    } catch (error) {
      toast.error('Failed to create note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    if (!editNoteContent.trim() || !onUpdateNote) {
      toast.error('Note content is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateNote(noteId, { note: editNoteContent.trim() });
      setEditingNoteId(null);
      setEditNoteContent('');
      toast.success('Note updated successfully!');
    } catch (error) {
      toast.error('Failed to update note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!onDeleteNote) return;
    
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await onDeleteNote(noteId);
        toast.success('Note deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete note');
      }
    }
  };

  const startEditingNote = (note: NoteItem) => {
    setEditingNoteId(note.id);
    setEditNoteContent(note.note);
    // Cancel create form if open
    setShowCreateForm(false);
    setNewNoteContent('');
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditNoteContent('');
  };

  const cancelCreate = () => {
    setShowCreateForm(false);
    setNewNoteContent('');
  };

  return (
    <div className="inline-notes-list">
      {/* Notes Header */}
      <div className="notes-header mb-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-3">
          <div className="mb-2 mb-sm-0">
            <h2 className="mb-1 notes-title" style={{ fontSize: '1.375rem' }}>Notes</h2>
            <small className="text-muted d-none d-sm-block">Manage your lead notes efficiently</small>
          </div>
          <Button
            variant={showCreateForm ? "secondary" : "primary"}
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setEditingNoteId(null);
              setEditNoteContent('');
            }}
            className="note-create-btn"
            startIcon={<FontAwesomeIcon icon={showCreateForm ? faTimes : faPlus} />}
          >
            {showCreateForm ? 'Cancel' : 'New Note'}
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 gap-sm-3 mb-3">
          <SearchBox
            placeholder="Search notes..."
            className="flex-grow-1"
            style={{ width: '100%', maxWidth: "30rem" }}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            prepend={<FontAwesomeIcon icon={faSearch} className="text-muted" />}
          />
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="notes-count-badge">
              {filteredNotes.length} {filteredNotes.length === 1 ? "note" : "notes"}
            </Badge>
          </div>
        </div>
        
        <hr className="my-2" />
      </div>

      {/* Inline Create Form */}
      <Collapse in={showCreateForm}>
        <div ref={createFormRef}>
          <Card className="mb-4 create-note-card border-primary">
            <Card.Body>
              <div className="d-flex align-items-start gap-3">
                <div className="note-icon-wrapper">
                  <UilClipboardNotes size="24" className="text-primary" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-2 text-primary">Create New Note</h6>
                  <Form.Control
                    ref={newNoteTextareaRef}
                    as="textarea"
                    rows={4}
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    placeholder="Write your note here..."
                    className="mb-2 inline-textarea"
                    maxLength={3000}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <small className={classNames(
                      "text-muted",
                      { "text-warning": newNoteContent.length > 2500 },
                      { "text-danger": newNoteContent.length > 2900 }
                    )}>
                      {newNoteContent.length}/3000 characters
                    </small>
                    <div className="d-flex gap-2">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={cancelCreate}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleCreateNote}
                        disabled={!newNoteContent.trim() || isSubmitting}
                        className="d-flex align-items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                        Save Note
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {/* Notes Feed */}
      {isLoading ? (
        <NotesSkeleton />
      ) : (
        <div className="notes-feed">
          {filteredNotes.length === 0 ? (
            <Card className="text-center py-5">
              <Card.Body>
                <UilClipboardNotes size="48" className="text-muted mb-3" />
                {searchQuery ? (
                  <>
                    <h5 className="text-muted">No notes found</h5>
                    <p className="text-muted mb-4">Try adjusting your search query</p>
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </Button>
                  </>
                ) : (
                  <>
                    <h5 className="text-muted">No notes yet</h5>
                    <p className="text-muted mb-4">Click "New Note" to create the first note</p>
                  </>
                )}
              </Card.Body>
            </Card>
          ) : (
            filteredNotes.map((note, index) => (
              <Card key={note.id} className={classNames("mb-3 note-card", {
                "editing-note": editingNoteId === note.id
              })}>
                <Card.Body className="note-card-body">
                  {editingNoteId === note.id ? (
                    // Inline Edit Form
                    <div className="d-flex align-items-start gap-3">
                      <div className="note-icon-wrapper">
                        <UilEdit size="20" className="text-primary" />
                      </div>
                      <div className="flex-grow-1">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={editNoteContent}
                          onChange={(e) => setEditNoteContent(e.target.value)}
                          className="mb-2 inline-textarea"
                          maxLength={3000}
                          autoFocus
                        />
                        <div className="d-flex justify-content-between align-items-center">
                          <small className={classNames(
                            "text-muted",
                            { "text-warning": editNoteContent.length > 2500 },
                            { "text-danger": editNoteContent.length > 2900 }
                          )}>
                            {editNoteContent.length}/3000 characters
                          </small>
                          <div className="d-flex gap-2">
                            <Button
                              variant="light"
                              size="sm"
                              onClick={cancelEditing}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleUpdateNote(note.id)}
                              disabled={!editNoteContent.trim() || isSubmitting}
                              className="d-flex align-items-center gap-1"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                              Update
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Note Display
                    <div className="d-flex flex-column">
                      <div className="note-content flex-grow-1">
                        <div className="d-flex align-items-center mb-3 note-header">
                          <div className="note-icon me-2">
                            <UilClipboardNotes size="20" className="text-primary" />
                          </div>
                          
                          <div className="note-meta flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Badge bg="info" className="note-badge d-flex align-items-center">
                                  Note
                                </Badge>
                                {index === 0 && filteredNotes.length === notes.length && (
                                  <Badge bg="success" className="d-flex align-items-center">
                                    Latest
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="mb-0 note-text">{note.note}</p>

                        <div className="note-footer d-flex flex-wrap align-items-center justify-content-between mt-3 gap-2">
                          <div className="note-author d-flex align-items-center">
                            <small className="text-muted note-date">
                              <FontAwesomeIcon icon={faUser} className="me-1" />
                              Created {dateFormatter(note.created_on)}
                            </small>
                          </div>
                          
                          <div className="note-actions">
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="text-decoration-none p-1"
                              onClick={() => startEditingNote(note)}
                              title="Edit note"
                            >
                              <FontAwesomeIcon icon={faPencilAlt} size="sm" />
                            </Button>
                            <Button 
                              variant="link" 
                              size="sm" 
                              className="text-decoration-none text-danger p-1"
                              onClick={() => handleDeleteNote(note.id)}
                              title="Delete note"
                            >
                              <UilTrash size="18" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default InlineNotesList;